import { Card, Row, Col, Modal, Form, Spinner, Button, InputGroup, FormLabel } from 'react-bootstrap';
import React, { useState, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/es';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { FaMapMarkerAlt, FaPlayCircle, FaSearch, FaFileExport, FaAngleRight, FaAngleLeft, FaAngleDoubleRight, FaAngleDoubleLeft } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import iconoPng from '../../../../../assets/img/icons/export.png';
import Flex from '../../../../../components/common/Flex';
import SmsIcon from '@mui/icons-material/Sms';
import EmailIcon from '@mui/icons-material/Email';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

// Activamos el soporte para formatos personalizados
dayjs.extend(customParseFormat);
dayjs.locale('es');


const ROW_HEIGHT = 65;
const HEADER_HEIGHT = 40;

const customStyles = `
.list-striped .alert-row:nth-child(odd) {
  background-color: rgba(250, 250, 250, 0.9);
}
.list-striped .alert-row:nth-child(even) {
  background-color: white;
}
.list-striped .alert-row:hover {
  background-color: rgba(245, 245, 245, 1);
}
.table-header {
  background-color: var(--falcon-200);
  font-weight: 600;
  font-size: 0.83rem;
  position: sticky;
  top: 0;
  z-index: 1;
}
.alert-row {
  padding: 0.5rem 0.5rem;
  font-size: 0.83rem;
  transition: background-color 0.15s ease;
}
.avatar-name.rounded-circle {
  display: flex;
  align-items: center;
  justify-content: center;
}
`;

const style2Lines = {
    display: "-webkit-box",
    WebkitLineClamp: 1, // Limita a 1 línea
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
};

const ConsumoTableListTanstack = ({
    data = [],
    columns = [],
    searchValue = '',
    onSearch,
    pageSize = 100,
    onPageSizeChange,
    isLoading = false,
    title = 'Listado de consumos',
    isGroup = false,
    isExporting = false,
    onExport,
    onTableRendered
}) => {
    // Estados para la tabla
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState(searchValue);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: pageSize,
    });

    // Sincronizar el filtro global con el searchValue recibido por props
    React.useEffect(() => {
        setGlobalFilter(searchValue);
    }, [searchValue]);

    // Sincronizar el tamaño de página con el pageSize recibido por props
    React.useEffect(() => {
        setPagination(prev => ({
            ...prev,
            pageSize
        }));
    }, [pageSize]);

    React.useEffect(() => {
        if (typeof onTableRendered === 'function') {
            onTableRendered(data.length);
        }
    }, [data.length]);


    const table = useReactTable({
        data,
        columns: columns,
        state: {
            sorting,
            globalFilter,
            pagination,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        enableSorting: true,
        enableGlobalFilter: true,
        debugTable: false,
    });

    // Referencias para virtualización
    const tableContainerRef = useRef(null);
    const { rows } = table.getRowModel();

    // Configurar el virtualizador
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: 10,
    });

    // Calcular la altura total de la tabla
    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();
    const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
    const paddingBottom = virtualRows.length > 0
        ? totalSize - virtualRows[virtualRows.length - 1].end
        : 0;

    // Manejar el cambio en la búsqueda global
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setGlobalFilter(value);
        if (onSearch) onSearch(value);
        // Resetear a la primera página cuando se busca
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
    };

    // Manejar cambio en el tamaño de página
    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPagination(prev => ({
            ...prev,
            pageSize: newSize,
        }));
        if (onPageSizeChange) onPageSizeChange(newSize);
    };

    // Manejar exportación a Excel
    const handleExportToExcel = async () => {
        if (window['guardarBitacoraExport']) {
            try {
                window['guardarBitacoraExport']();
            } catch (error) {

            }
        }
        if (typeof onExport === 'function') {
            onExport();
            return;
        }

        try {
            // Default export: exporta todos los datos filtrados
            const dataToExport = table.getFilteredRowModel().rows.map(row => {
                return {
                    Alerta: row.original.nombrealerta,
                    Vehiculo: row.original.vehiculo,
                    Fecha: row.original.fecha, title,
                    Destinarario: row.original.destinatario,
                    Mensaje: row.original.mensaje,
                    Consumo: row.original.consumo
                };
            });

            const ws = XLSX.utils.json_to_sheet(dataToExport);
            const wb = XLSX.utils.book_new();

            // Agregar título
            XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: "A1" });

            // Combinar celdas para el título
            if (!ws['!merges']) ws['!merges'] = [];
            ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } });

            // Ajustar anchos de columna
            const colWidths = [];
            for (let i = 0; i < 5; i++) colWidths.push({ wch: 15 });
            ws['!cols'] = colWidths;

            XLSX.utils.book_append_sheet(wb, ws, "Consumos");
            XLSX.writeFile(wb, "ReporteDeConsumos.xlsx");
        } catch (error) {
            console.error("Error al exportar a Excel:", error);
        }
    };


    const prepareForPrint = useCallback(() => {
        console.log('🖨️ Preparando tabla para impresión...');

        if (tableContainerRef.current) {
            const container = tableContainerRef.current;

            // ✅ EXPANDIR CONTENEDOR
            container.style.height = 'auto';
            container.style.maxHeight = 'none';
            container.style.overflow = 'visible';

            // ✅ MARCAR COMO MODO IMPRESIÓN
            container.setAttribute('data-print-mode', 'true');
            container.classList.add('print-mode');
        }

        // ✅ FORZAR RENDERIZADO DE TODAS LAS FILAS
        setPagination(prev => ({
            ...prev,
            pageSize: data.length, // Mostrar todos los datos
            pageIndex: 0
        }));

        return new Promise(resolve => {
            // Esperar que se apliquen los cambios
            setTimeout(() => {
                console.log('✅ Tabla preparada para impresión');
                resolve();
            }, 500);
        });
    }, [data.length]);

    const restoreAfterPrint = useCallback(() => {
        console.log('🔄 Restaurando tabla después de impresión...');

        if (tableContainerRef.current) {
            const container = tableContainerRef.current;

            // ✅ RESTAURAR ESTILOS ORIGINALES
            container.style.height = 'calc(700px - 220px)';
            container.style.maxHeight = '';
            container.style.overflow = 'auto';

            // ✅ QUITAR MODO IMPRESIÓN
            container.removeAttribute('data-print-mode');
            container.classList.remove('print-mode');
        }

        // ✅ RESTAURAR PAGINACIÓN
        setPagination(prev => ({
            ...prev,
            pageSize: pageSize, // Volver al tamaño original
            pageIndex: 0
        }));
    }, [pageSize]);

    React.useEffect(() => {
        window.prepareTableForPrint = prepareForPrint;
        window.restoreTableAfterPrint = restoreAfterPrint;

        return () => {
            delete window.prepareTableForPrint;
            delete window.restoreTableAfterPrint;
        };
    }, [prepareForPrint, restoreAfterPrint]);

    return (

        <Card className="h-100">
            <Card.Header>
                <Row className="d-flex align-items-center justify-content-between">
                    <Col md={6}>
                        <h6 className="mb-0 text-nowrap py-2 py-xl-0">{title}</h6>
                    </Col>
                    <Col md={4} className="d-flex justify-content-end align-items-center">
                        <Button
                            variant="primary"
                            size="sm"
                            className="me-2 p-2"
                            onClick={handleExportToExcel}
                            disabled={isExporting || rows.length === 0}
                        >
                            {isExporting ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-1"
                                    />
                                    Exportando...
                                </>
                            ) : (
                                <>
                                    <img
                                        src={iconoPng}
                                        alt="Exportar" title='Exportar a Excel'
                                        style={{ width: '20px', height: '20px', filter: 'brightness(0) invert(1)' }} // Ajusta el tamaño
                                        className="me-1 ms-1"
                                    />
                                    {/* <FaFileExport className="me-1 ms-1" /> */}
                                </>
                            )}
                        </Button>

                        <InputGroup size="sm" style={{ maxWidth: 200 }}>
                            {/* <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text> */}
                            <Form.Control
                                className='p-2'
                                type="text"
                                placeholder="Buscar..."
                                value={globalFilter || ''}
                                onChange={handleSearchChange}
                            />
                            {globalFilter && (
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => {
                                        setGlobalFilter('');
                                        if (onSearch) onSearch('');
                                    }}
                                >
                                    &times;
                                </Button>
                            )}
                        </InputGroup>
                    </Col>
                    <Col md={2}>

                        <Form className='d-flex p-2 flex-row-reverse'>
                            <Form.Select
                                className='p-2'
                                size="sm"
                                style={{ width: '100px' }}
                                value={table.getState().pagination.pageSize}
                                onChange={handlePageSizeChange}
                            >
                                <option value="10">10</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option value="250">250</option>
                                <option value="500">500</option>
                                <option value="1000">1000</option>
                            </Form.Select>
                        </Form>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body className="p-0">
                <style>{customStyles}</style>
                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <Spinner animation="border" role="status" variant="primary">
                            <span className="visually-hidden">Cargando...</span>
                        </Spinner>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="text-center p-5">No hay datos para mostrar</div>
                ) : (
                    <div style={{ overflow: 'auto' }}>
                        <div style={{ minWidth: 1100 }}>
                            {/* Cabecera de la tabla - fija */}
                            <div className="d-flex table-header p-3 ps-3 text-nowrap align-middle">
                                <div style={{ width: '17%', marginLeft: '4px' }}>Alerta</div>
                                <div style={{ width: '15%' }}>Vehículo</div>
                                <div style={{ width: '10%' }}>Fecha</div>
                                <div style={{ width: '15%' }}>Destinatario</div>
                                <div style={{ width: '35%' }}>Mensaje</div>
                                <div style={{ width: '10%' }}>Consumo</div>
                            </div>

                            {/* Contenedor de tabla con altura fija y scroll */}
                            <div
                                ref={tableContainerRef}
                                style={{
                                    height: 'calc(65vh - 40px)',
                                    overflow: 'auto',
                                }}
                                className="list-striped"
                            >
                                <div
                                    style={{
                                        height: `${totalSize}px`,
                                        width: '100%',
                                        position: 'relative',
                                    }}
                                >
                                    {virtualRows.map(virtualRow => {

                                        const row = rows[virtualRow.index];
                                        console.log(row);
                                        return (
                                            <div
                                                key={row.id}
                                                className="alert-row d-flex"
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: `${ROW_HEIGHT}px`,
                                                    transform: `translateY(${virtualRow.start}px)`,
                                                    alignItems: 'center',
                                                    borderBottom: '1px solid #f5f5f5',
                                                }}
                                            >
                                                <div style={{ width: '17%', marginLeft: '12px', }}>
                                                    {<Flex> {/* Ajustar ancho máximo del contenedor */}
                                                        <span
                                                            style={style2Lines}
                                                            title={row.original.nombrealerta} // Mostrar tooltip con el texto completo
                                                        >
                                                            {row.original.nombrealerta}
                                                        </span>
                                                    </Flex>}
                                                </div>
                                                <div style={{ width: '15%' }}>
                                                    {<Flex> {/* Ajustar ancho máximo del contenedor */}
                                                        <span
                                                            style={style2Lines}
                                                            title={row.original.vehiculo} // Mostrar tooltip con el texto completo
                                                        >
                                                            {row.original.vehiculo}
                                                        </span>
                                                    </Flex>}
                                                </div>
                                                <div style={{ width: '10%' }}>
                                                    <Flex>
                                                        <span
                                                            style={style2Lines}
                                                            title={row.original.fechaHoraEvento} // Mostrar tooltip con el texto completo
                                                        >
                                                            {row.original.fechaHoraEvento}
                                                        </span>
                                                    </Flex>
                                                </div>
                                                <div style={{ width: '10%', marginLeft: '12px', }}>
                                                    {<Flex> {/* Ajustar ancho máximo del contenedor */}
                                                        <span
                                                            style={style2Lines}
                                                            title={row.original.destino} // Mostrar tooltip con el texto completo
                                                        >
                                                            {row.original.destinatario}
                                                        </span>
                                                    </Flex>}
                                                </div>
                                                <div style={{ width: '37%' }}>
                                                    {
                                                        <Flex> {/* Ajustar ancho máximo del contenedor */}
                                                            <span
                                                                style={style2Lines}
                                                                title={row.original.mensaje} // Mostrar tooltip con el texto completo
                                                            >
                                                                {row.original.mensaje}
                                                            </span>
                                                        </Flex>
                                                    }
                                                </div>
                                                <div style={{ width: '10%', marginLeft: '12px', }}>
                                                    {

                                                        <Flex> {/* Ajustar ancho máximo del contenedor */}
                                                            <span
                                                                style={style2Lines}
                                                                title={row.original.consumo} // Mostrar tooltip con el texto completo
                                                            >
                                                                {row.original.consumo}
                                                            </span>
                                                        </Flex>
                                                    }
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Card.Body>

            <Card.Footer className="bg-white">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                    <div className="d-flex align-items-center">
                        <span className="me-2">
                            Total: {table.getFilteredRowModel().rows.length} | Página {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
                        </span>
                        <Form.Control
                            type="number"
                            min="1"
                            max={table.getPageCount()}
                            value={table.getState().pagination.pageIndex + 1}
                            onChange={e => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                table.setPageIndex(page);
                            }}
                            style={{ width: '70px', height: '31px' }}
                            size="sm"
                        />
                    </div>
                    <div className="d-flex justify-content-center">
                        <Button
                            variant="outline-dark"
                            size="sm"
                            className="mx-1"
                            onClick={() => table.setPageIndex(0)}
                            disabled={!table.getCanPreviousPage()}
                        >
                            <FaAngleDoubleLeft />
                            {/* &laquo; Primera */}
                        </Button>
                        <Button
                            variant="outline-dark"
                            size="sm"
                            className="mx-1"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                        >
                            <FaAngleLeft />
                            {/* &lt; Anterior */}
                        </Button>

                        {/* Mostrar números de página (5 páginas alrededor de la actual) */}
                        {(() => {
                            // Determinar qué páginas mostrar
                            let pagesArray = [];
                            if (table.getPageCount() <= 5) {
                                // Si hay 5 o menos páginas, mostrarlas todas
                                pagesArray = Array.from({ length: table.getPageCount() }, (_, i) => i);
                            } else if (table.getState().pagination.pageIndex <= 2) {
                                // Si estamos en las primeras páginas
                                pagesArray = [0, 1, 2, 3, 4];
                            } else if (table.getState().pagination.pageIndex >= table.getPageCount() - 3) {
                                // Si estamos en las últimas páginas
                                pagesArray = [
                                    table.getPageCount() - 5,
                                    table.getPageCount() - 4,
                                    table.getPageCount() - 3,
                                    table.getPageCount() - 2,
                                    table.getPageCount() - 1
                                ];
                            } else {
                                // Estamos en medio, mostrar 2 antes y 2 después
                                pagesArray = [
                                    table.getState().pagination.pageIndex - 2,
                                    table.getState().pagination.pageIndex - 1,
                                    table.getState().pagination.pageIndex,
                                    table.getState().pagination.pageIndex + 1,
                                    table.getState().pagination.pageIndex + 2
                                ];
                            }

                            return pagesArray.map(pageNum => (
                                <Button
                                    key={`page-${pageNum}`}
                                    variant={pageNum === table.getState().pagination.pageIndex ? "primary" : "outline-dark"}
                                    size="sm"
                                    className="mx-1"
                                    onClick={() => table.setPageIndex(pageNum)}
                                >
                                    {pageNum + 1}
                                </Button>
                            ));
                        })()}

                        <Button
                            variant="outline-dark"
                            size="sm"
                            className="mx-1"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                        >
                            {/* Siguiente &gt; */}
                            <FaAngleRight />
                        </Button>
                        <Button
                            variant="outline-dark"
                            size="sm"
                            className="mx-1"
                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                            disabled={!table.getCanNextPage()}
                        >
                            {/* Última &raquo; */}
                            <FaAngleDoubleRight />
                        </Button>
                    </div>
                </div>
            </Card.Footer>
        </Card>

    );

}

export { ConsumoTableListTanstack };